'use client';

import { CollectionType, ContentCollectionType } from '@/@types';
import { Avatar, Button, Icon, ScreenContainer } from '@/components/core';
import { UIComponent } from '@/components/core/components';
import { useUser } from '@/components/context/user';
import FollowButton from '@/components/core/components/organism/follow-button';
import InternalLink from '@/components/core/components/organism/internal-link';
import { useLocalizedLinks, useMediaQuery } from '@/components/core/hooks';
import { useCallback, useMemo, useState } from 'react';
import ResultGrid from '@/components/core/components/molecules/result-grid';
import Feed from '@/components/core/components/organism/feed';
import { FeedGroupContext } from '@/components/core/components/organism/feed/components/context/context';
import { paginationToArray } from '@/services/api/namespaces/_utils';
import { gql } from '@/services/api';
import * as styles from './collection-screen.styles';
import Breadcrumbs from './_components/breadcrumb';
import * as queries from './collection.queries';

const CollectionScreen: UIComponent<{ collection: CollectionType }> = ({ collection }) => {
  const { user, api } = useUser();
  const links = useLocalizedLinks();
  const isMine = user && collection.owner?.id === user?.id;
  const mediaQuery = useMediaQuery();
  const [playing, setPlaying] = useState<string | null>(null);

  const contextValue = useMemo(() => {
    return {
      playing,
    };
  }, [playing]);

  const [content, setContent] = useState<ContentCollectionType[]>(paginationToArray(collection.content!));
  const [pageInfo, setPageInfo] = useState(collection.content!.pageInfo);

  const onLoadMore = useCallback(async () => {
    const more = (
      await api.graphqlRequest<{ Collection: { get: CollectionType } }>({
        query: gql`
          ${queries.CollectionFragment}
          ${queries.ContentFragment}
          query getCollection($id: ID!, $after: ID) {
            Collection {
              get(id: $id) {
                ...CollectionFragment
                ...ContentFragment
              }
            }
          }
        `,
        variables: { id: collection.id, after: pageInfo!.endCursor },
      })
    ).data.Collection.get;
    setContent((c) => {
      return c.concat(paginationToArray(more.content!));
    });
    setPageInfo(more.content!.pageInfo);
  }, [api, collection.id, pageInfo]);

  return (
    <FeedGroupContext.Provider value={contextValue}>
      <ScreenContainer className={styles.root}>
        <Breadcrumbs collection={collection} />
        <div className={styles.header}>
          <h1 className={styles.title}>{collection.name}</h1>
          <h3 className={styles.user}>
            <Avatar size={32} src={collection.owner?.avatar} level={collection.owner?.level?.name} />
            <InternalLink href={links.profile(collection.owner!).root} lines={1} className={styles.nickname}>
              {collection.owner?.nickname}
            </InternalLink>
          </h3>
          <div className={styles.actions}>
            <div>
              {isMine ? (
                <Button variant="tertiary" onClick={() => {}}>
                  <Icon name="analytics" />
                </Button>
              ) : (
                <FollowButton as={Button} size="small" />
              )}
              <Button
                variant="tertiary"
                onClick={() => {
                  navigator.share({
                    title: collection.name,
                    url: `/collection/${collection.id}`,
                  });
                }}
              >
                <Icon name="share" />
              </Button>
            </div>
          </div>
        </div>
        {collection.description && <div className={styles.description}>{collection.description}</div>}
        <ResultGrid minCols={1} maxCols={4} hasMore={pageInfo!.hasNextPage} onLoadMore={onLoadMore}>
          {content.map((item) => {
            const reco = item.recommendation;
            const hasVideo =
              reco?.media.some((media) => {
                return media.type === 'video';
              }) ||
              item.product.media.some((media) => {
                return media.type === 'video';
              });
            return (
              <Feed
                {...(hasVideo && {
                  onMouseEnter: () => {
                    if (hasVideo) {
                      setPlaying(item.product.id);
                    }
                  },
                  onMouseLeave: () => {
                    setPlaying(null);
                  },
                  onViewport: (isOnViewport) => {
                    if (isOnViewport && hasVideo && !mediaQuery.up('md')) {
                      setPlaying(item.product.id);
                    }
                  },
                })}
                key={item.id}
                product={item.product}
                recommendation={item.recommendation}
              />
            );
          })}
        </ResultGrid>
      </ScreenContainer>
    </FeedGroupContext.Provider>
  );
};

export default CollectionScreen;
